import { useEffect, useState } from "react";
import type { AgendaSettings } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import type { FormValues } from "./formulario-da-consulta";

export type SalaDaOpcao = { id: string; nome: string; cor: string | null };

/**
 * A sala da consulta. Na criação vem a sala padrão do profissional naquele dia da semana, e segue o
 * profissional e a data enquanto ninguém escolher à mão. Na edição, fica a que a consulta tem —
 * inclusive uma sala já desativada, que continua na lista só pra essa consulta.
 */
export function useSalaDaConsulta(p: {
  open: boolean;
  mode: "create" | "edit";
  values: FormValues;
  update: (v: Partial<FormValues>) => void;
  settings: AgendaSettings;
  salaAtual?: SalaDaOpcao | null;
  /** A sala já veio escolhida (duplo clique na coluna da agenda por sala): não troca pela padrão. */
  fixada?: boolean;
}) {
  const { open, mode, values, update, settings } = p;
  const ativas = (trpc.salas.listar.useQuery(undefined, { enabled: open, staleTime: 60_000 }).data ?? []) as SalaDaOpcao[];
  const salas = p.salaAtual && !ativas.some((s) => s.id === p.salaAtual!.id) ? [...ativas, p.salaAtual] : ativas;
  // Escolha à mão nesta abertura. Edição e sala vinda de fora travam desde o primeiro render: com as
  // salas já em cache, a sugestão rodava antes do estado virar e trocava a sala da consulta.
  const travada = mode === "edit" || !!p.fixada;
  const [escolhida, setEscolhida] = useState(false);
  useEffect(() => {
    if (open) setEscolhida(false);
  }, [open]);

  useEffect(() => {
    if (!open || travada || escolhida || !ativas.length) return;
    const dia = new Date(`${values.date}T00:00:00`).getDay();
    const padrao = values.dentistId ? settings.dentistSettings?.[values.dentistId]?.salaPorDia?.[String(dia)] : undefined;
    update({ salaId: ativas.some((s) => s.id === padrao) ? padrao! : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, travada, escolhida, values.dentistId, values.date, ativas.length]);

  return {
    salas,
    escolher: (id: string) => {
      setEscolhida(true);
      update({ salaId: id || null });
    },
  };
}
